import React from "react";
import _ from 'lodash';
import moment from "moment";

import {makeStyles} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";

import {renderQuestion} from "../../../../../utils";
import FormLabelElement from "../../formElement/LabelFormElement/LabelFormElement";
import CheckboxQuestion from "../../question/CheckboxQuestion/CheckboxQuestion";

import {
  CLEARING_ACCOUNT_BALANCING_QUESTION_UID,
  CLEARING_ACCOUNT_OPTION_QUESTION_UID,
  CLEARING_ACCOUNT_QUESTION_UID,
  QUESTION_TYPE
} from "../../../../../constants";
import RadioCard from "../../formElement/RadioCard";
import {BACKEND_DATE_FORMAT, withEuroOrDash} from "../../../../../../../utils/utils";
import {DATE_FORMAT} from "../../../../../../../utils/constants";
import InfoCard from "../DynamicStep/components/InfoCard";

import styles from "../styles";


const useStyles = makeStyles(styles);
const usePageStyles = makeStyles((theme) => ({
  stepInfoText: {
    paddingTop: `${theme.spacing(1)}px !important`,
    paddingBottom: `${theme.spacing(1)}px !important`,
  }
}));

const ACCOUNT_TAB_CONFIG = {
  reference: {
    key: 'reference',
    label: 'Referenzkonto',
    info: 'Bestehende Bankverbindung des Kunden. Abbuchung und Auszahlung laufen über dieses Konto.',
    headline: 'Referenzkonto',
  },
  clearing: {
    key: 'clearing',
    label: 'Verrechnungskonto',
    info: 'Konto bei der Depotbank. Orders werden aus dem dort verfügbaren Saldo bedient.',
    headline: 'Verrechnungskonto',
  },
};
const ACCOUNT_TABS = Object.values(ACCOUNT_TAB_CONFIG);

// Static style objects to avoid re-creation on every render
const CONTAINER_STYLE = {paddingTop: 17};
const TABS_CONTAINER_STYLE = {textAlign: 'center'};
const INFO_STYLE = {lineHeight: 1.5};
const CONFIRMATION_STYLE = {marginTop: 8};

const updateOptionalFlagAndError = (question, optional) => {
  question.optional = optional;
  question._ignore = optional;
  if(optional){
    delete question.error;
  }
}

/**
 * Per-depot account selection step for Trading V2.
 * Two tabs: "Referenzkonto auswählen" (default) / "Verrechnungskonto auswählen"
 * - Reference tab: shows reference account cards (RadioCard selection)
 * - Clearing tab: shows clearing account cards (RadioCard selection)
 */
const DepotAccountStepV2 = props => {
  const {
    onAnswerChange,
    questions,
    dataService,
  } = props;

  const classes = useStyles();

  const {clearing_account, clearing_account_option, clearing_account_balancing, bank_account} = React.useMemo(() => {
    const step = {question: questions};
    return {
      clearing_account: dataService.findQuestionDeep(step, CLEARING_ACCOUNT_QUESTION_UID),
      clearing_account_option: dataService.findQuestionDeep(step, CLEARING_ACCOUNT_OPTION_QUESTION_UID),
      clearing_account_balancing: dataService.findQuestionDeep(step, CLEARING_ACCOUNT_BALANCING_QUESTION_UID),
      bank_account: questions.find(q => q.type === QUESTION_TYPE.REFERENCE_ACCOUNTS_V2),
    };
  }, [questions]);

  const clearingAccountsExist = clearing_account_option.question.length > 0;
  const optionQuestion = clearing_account_option.question[0];
  const confirmationQuestion = clearing_account_balancing.question[0];

  const choicesById = React.useMemo(() => {
    if (!optionQuestion) return {};
    return _.keyBy(optionQuestion.config.choices, 'uid');
  }, [_.get(optionQuestion, 'config.choices')]);

  const availableTabs = React.useMemo(() => {
    const tabs = !clearingAccountsExist
      ? ACCOUNT_TABS.filter(tab => tab.key === ACCOUNT_TAB_CONFIG.reference.key)
      : ACCOUNT_TABS

    return tabs.map(tab => ({
      label: <TabLabel label={tab.label} info={tab.info}/>,
      id: tab.key,
      value: tab.key
    }));
  }, [clearingAccountsExist]);

  const [activeTab, setActiveTab] = React.useState(() => {
    if (clearingAccountsExist && _.some(clearing_account.question, q => q.answer)) {
      return ACCOUNT_TAB_CONFIG.clearing.key;
    }
    return ACCOUNT_TAB_CONFIG.reference.key;
  });

  // Derive optional flags from activeTab on every render.
  // These are cheap property assignments on mutable question objects — no state updates.
  const syncOptionalFlags = (currentTab) => {
    const isRefTab = currentTab === ACCOUNT_TAB_CONFIG.reference.key;

    if (optionQuestion) {
      updateOptionalFlagAndError(optionQuestion, isRefTab);

      if (confirmationQuestion) {
        const choice = optionQuestion.answer ? choicesById[optionQuestion.answer] : null;
        updateOptionalFlagAndError(confirmationQuestion, isRefTab || !choice || !choice.notEnoughMoney);
      }
    }

    updateOptionalFlagAndError(bank_account, !isRefTab);
  };

  syncOptionalFlags(activeTab);

  const handleTabChange = React.useCallback((newTab) => {
    setActiveTab(prev => {
      if (newTab === prev) return prev;

      const clearingQuestion = clearing_account.question[0];
      onAnswerChange(clearingQuestion.uid, newTab !== ACCOUNT_TAB_CONFIG.reference.key);

      return newTab;
    });
  }, [clearing_account, onAnswerChange]);

  const onClearingAccountOptionAnswerChange = React.useCallback((value) => {
    optionQuestion.error = null;
    onAnswerChange(optionQuestion.uid, value);

    // Reset confirmation answer when selection changes — flags will re-derive on next render
    if (confirmationQuestion) {
      confirmationQuestion.error = null;
      onAnswerChange(confirmationQuestion.uid, null);
    }
  }, [confirmationQuestion, optionQuestion, onAnswerChange]);

  const hasSelection = availableTabs.length > 1;

  return (
    <>
      <div className={classes.stepName}>
        {hasSelection
          ? 'Bitte wählen Sie ein Konto für die Abwicklung aus'
          : `Bitte wählen Sie ein ${ACCOUNT_TAB_CONFIG[activeTab].headline} aus`
        }
      </div>
      <Grid container spacing={3} style={CONTAINER_STYLE}>
        {hasSelection && (
          <Grid item xs={12} style={TABS_CONTAINER_STYLE}>
            <RadioCard
              value={activeTab}
              options={availableTabs}
              onChange={handleTabChange}
              newDesign
              custom_classes={{
                radioGroupRoot: 'MuiGrid-container',
                formControlLabelRoot: 'MuiFormControlLabel-root MuiGrid-item'
              }}
            />
          </Grid>
        )}

        {activeTab === ACCOUNT_TAB_CONFIG.reference.key && (
          renderQuestion(bank_account, onAnswerChange, dataService)
        )}

        {activeTab === ACCOUNT_TAB_CONFIG.clearing.key && (
          <>
            <Grid item xs={12}>
              <FormLabelElement
                style={INFO_STYLE}
                label={"Bitte beachten Sie, dass es sich bei diesem Wert um den aktuellen Kontostand handelt. " +
                "Dispositionen der Depotbank können hier nicht mit berücksichtigt werden und ggf. " +
                "zu einem anderen Ausweis auf der Depotbank führen."} />
            </Grid>
            <ClearingAccountCards
              optionQuestion={optionQuestion}
              confirmationQuestion={confirmationQuestion}
              onClearingAccountOptionAnswerChange={onClearingAccountOptionAnswerChange}
              onAnswerChange={onAnswerChange}
              classes={classes}
            />
          </>
        )}

        {!!bank_account.bank_warn && (
          <Grid item xs={12}>
            <InfoCard isNewDesign info_text={bank_account.bank_warn} uid={'bank-warning'}/>
          </Grid>
        )}
      </Grid>
    </>
  );
};

const useTabStyles = makeStyles((theme) => ({
  container: {
    textAlign: 'left',
  },
  info: {
    marginTop: 4,
    fontSize: '0.875em',
    fontFamily: 'Roboto-Regular',
  }
}));

const TabLabel = ({label, info}) => {
  const classes = useTabStyles();

  return (
    <div className={classes.container}>
      <div className={classes.label}><b>{label}</b></div>
      <div className={classes.info}>{info}</div>
    </div>
  )
}

/** Clearing account card selection for a single depot */
const ClearingAccountCards = (({optionQuestion, confirmationQuestion, onClearingAccountOptionAnswerChange, onAnswerChange, classes}) => {
  const choices = React.useMemo(() => optionQuestion.config.choices.map(choice => ({
    value: choice.uid,
    label: choice.text,
  })), [optionQuestion.config.choices])

  return (
    <Grid item xs={12}>
      <RadioCard
        value={optionQuestion.answer || ''}
        options={choices}
        onChange={onClearingAccountOptionAnswerChange}
        error={optionQuestion.error}
        newDesign
      />
      {confirmationQuestion && !confirmationQuestion.optional && (
        <Grid container spacing={1} style={CONFIRMATION_STYLE}>
          <Grid item xs={12} className={classes.checkboxRegularContainer}>
            <CheckboxQuestion
              question={confirmationQuestion}
              onAnswerChange={onAnswerChange}
            />
          </Grid>
        </Grid>
      )}
    </Grid>
  );
});

export default DepotAccountStepV2;

export const CashAccountCard = ({account, portfolioName, notEnoughMoney, asBankAccount, logo}) => {
  const classes = usePageStyles();

  return (
    <Grid container spacing={3} alignItems={"center"} style={{textAlign: 'left'}}>
      <Grid item xs={'auto'} style={{width: 124}}>
        <img src={logo} alt={portfolioName} style={{maxWidth: '100%'}} />
      </Grid>
      <Grid item xs>
        <Grid container>
          <Grid item xs={12}>
            <Grid container spacing={1}>
              <Grid item xs={'auto'} style={{minWidth: 110}}><b>Kreditinstitut:</b></Grid>
              <Grid item xs>{portfolioName}</Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={1}>
              <Grid item xs={'auto'} style={{minWidth: 110}}><b>Konto-Nr.:</b></Grid>
              <Grid item xs>{account.account_number}</Grid>
            </Grid>
          </Grid>
          {asBankAccount && !!account.iban && (
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={'auto'} style={{minWidth: 110}}><b>IBAN:</b></Grid>
                <Grid item xs>{account.iban}</Grid>
              </Grid>
            </Grid>
          )}
          <Grid item xs={12}>
            <Grid container spacing={1}>
              <Grid item xs={'auto'} style={{minWidth: 110}}><b>Saldo:</b></Grid>
              <Grid item xs>{withEuroOrDash(account.value)}</Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={1}>
              <Grid item xs={'auto'} style={{minWidth: 110}}><b>Datum:</b></Grid>
              <Grid item xs>{moment(account.value_date, BACKEND_DATE_FORMAT).format(DATE_FORMAT)}</Grid>
            </Grid>
          </Grid>
          {notEnoughMoney && (
            <Grid item xs={12}>
              <InfoCard isNewDesign cardType={'warning'} classes={{stepInfoText: classes.stepInfoText}}>
                Der aktuelle Saldo reicht nicht aus, um alle Orders auszuführen.
              </InfoCard>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
}
